import React, { useState, useEffect } from "react";
import Footer from "../components/footer";
import { motion } from "framer-motion";
import { useLanguage } from "../components/idiomas";

export default function TeamPage() {
  const { language, translations } = useLanguage();
  const t = translations?.[language]?.teamPage;

  // === Estado expandido, reactivo al cambio de idioma ===
  const [expandido, setExpandido] = useState([]);

  useEffect(() => {
    if (t?.members) {
      setExpandido(Array(t.members.length).fill(false));
    }
  }, [t]);

  // === Evita render vacío mientras cargan traducciones ===
  if (!t)
    return (
      <div className="flex items-center justify-center h-screen text-gray-500 text-xl">
        Cargando contenido...
      </div>
    );

  const toggleExpand = (index) => {
    const nuevos = [...expandido];
    nuevos[index] = !nuevos[index];
    setExpandido(nuevos);
  };

  const valores = t.missionVision.valuesList;

  return (
    <div className="min-h-screen flex flex-col font-sans bg-[#ffffff] text-[#1E2E45]">
      {/* === ENCABEZADO === */}
      <div className="w-full h-32 bg-[#0B1C2C] flex items-center justify-center">
        <h2 className="text-3xl md:text-4xl font-bold text-white">
          Legal Innova Costa Rica
        </h2>
      </div>

      {/* === SECCIÓN 1: Quiénes somos === */}
      <section className="relative px-6 md:px-20 py-24 overflow-hidden">
        <div
          className="absolute inset-0 bg-gradient-to-br from-[#F4F1ED] via-[#F9FAFB] to-[#E6E6E6]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(230,90,44,0.1), transparent 60%), radial-gradient(circle at 80% 70%, rgba(30,46,69,0.1), transparent 70%)",
            zIndex: 0,
          }}
        ></div>

        <div className="relative z-10 grid md:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ x: -80, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            viewport={{ once: true }}
            className="space-y-6 text-left md:text-justify"
          >
            <h2 className="text-3xl md:text-3xl font-bold text-[#1E2E45]">
              {t.mainTitle}
            </h2>

            <p className="text-lg leading-relaxed text-[#1E2E45]/90 mt-4">
              {t.subtitle}
            </p>

            <p className="text-lg leading-relaxed text-[#1E2E45]/90 mt-4">
              {t.subtitleSub}
            </p>

            <div className="h-1 w-32 bg-[#1E2E45] rounded-full mt-4"></div>
          </motion.div>

          <motion.div
            initial={{ x: 80, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            viewport={{ once: true }}
            className="flex justify-center md:justify-end"
          >
            <div className="relative w-auto flex justify-center md:justify-end md:mr-28 mr-4">
              <div className="transform md:-translate-x-4 -translate-x-0">
                <img
                  src="/images/fotoia.jpeg"
                  alt="Legal Center Logo"
                  className="object-contain rounded-lg transition-transform duration-500 hover:scale-105"
                  style={{ width: "22rem", minWidth: "12rem" }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === SECCIÓN 2: Nuestros Socios === */}
      <section
        className="relative px-6 md:px-20 py-24 overflow-hidden text-white bg-black bg-fixed bg-cover bg-center"
        style={{ backgroundImage: "url('/images/FondoTeam.png')" }}
      >
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: -40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-white mb-4"
          >
            {t.teamTitle}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-lg text-gray-300"
          >
            {t.teamSubtitle}
          </motion.p>
        </div>

        <div className="relative z-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3 justify-items-center">
          {t.members.map((socio, index) => {
            const texto = socio.bio;
            const textoCorto = texto.split(".").slice(0, 2).join(".") + ".";
            const isExpanded = expandido[index];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-[#111111]/90 rounded-2xl shadow-lg hover:shadow-white/30 transition-all duration-500 p-8 text-center flex flex-col items-center max-w-xs border border-white/10"
              >
                <img
                  src={socio.image}
                  alt={socio.name}
                  className="w-24 h-24 object-cover rounded-full mb-4 border-4 border-[#F4C542] shadow-white/30 shadow-md"
                />
                <h3 className="text-lg font-semibold text-white mb-1">
                  {socio.name}
                </h3>
                <p className="text-white/70 font-medium mb-2 text-sm">
                  {socio.role}
                </p>

                <p className="text-gray-400 leading-relaxed text-sm mb-3">
                  {isExpanded ? texto : textoCorto}
                </p>

                {/* === BOTÓN MULTILENGUAJE === */}
                <button
                  onClick={() => toggleExpand(index)}
                  className="mt-auto px-6 py-2.5 text-sm font-semibold rounded-md border border-[#0B1C2C] bg-[#0B1C2C]/80 text-white hover:bg-white hover:text-[#0B1C2C] transition-all duration-300 ease-in-out shadow-md hover:shadow-[#0B1C2C]/40 hover:-translate-y-0.5"
                >
                  {isExpanded
                    ? {
                        es: "Leer menos",
                        en: "Read less",
                        fr: "Lire moins",
                      }[language]
                    : {
                        es: "Leer más",
                        en: "Read more",
                        fr: "Lire plus",
                      }[language]}
                </button>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* === SECCIÓN 3: Misión, Visión, Valores === */}
      <section
        className="relative px-6 md:px-20 py-24 overflow-hidden bg-cover bg-fixed bg-center bg-no-repeat"
        
      >
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          viewport={{ once: true }}
          className="relative z-10 grid md:grid-cols-2 gap-16 items-center"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            viewport={{ once: true }}
            className="relative flex justify-start items-center ml-24 sm:ml-10 md:ml-16 lg:ml-24"
          >
            <img
              src="/images/logosimple.png"
              alt="Legal Center Emblema"
              className="relative z-10 w-48 sm:w-52 md:w-56 lg:w-64 object-contain transition-transform duration-500 hover:scale-105 drop-shadow-[0_0_35px_rgba(255,255,255,0.99)]"
            />
          </motion.div>

          <div className="text-left leading-relaxed space-y-10">
            <motion.div
              initial={{ x: 80, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="font-semibold text-lg mb-3 text-[#1E2E45] uppercase tracking-wide">
                {t.missionVision.visionTitle}
              </h3>
              <p className="text-lg text-[#1E2E45]">
                {t.missionVision.visionText}
              </p>
            </motion.div>

            <motion.div
              initial={{ x: 80, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3 className="font-semibold text-lg mb-3 text-[#1E2E45] uppercase tracking-wide">
                {t.missionVision.missionTitle}
              </h3>
              <p className="text-lg text-[#1E2E45]">
                {t.missionVision.missionText}
              </p>
            </motion.div>

            <motion.div
              initial={{ x: 80, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h3 className="font-semibold text-lg mb-3 text-[#1E2E45] uppercase tracking-wide">
                {t.missionVision.valuesTitle}
              </h3>
              <p className="mb-8 text-[#1E2E45] text-lg">
                {t.missionVision.valuesIntro}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-y-3 text-[#1E2E45] text-base">
                {valores.map((valor, idx) => (
                  <p key={idx}>✔ {valor}</p>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
}